import React, { Fragment, forwardRef, useMemo } from 'react';
import { useContext, useEffect, useRef, useState, useImperativeHandle } from 'react';
import './pageHeaderNav.scss';

import { LANG_ENUM } from '@umu/umu-lang-switch';
import { SYSTEM } from '../../base/contextService';
import { MarketEntryTip } from '../marketTip';
import { NavItem } from '../navItem/navItem';
import { PlainText } from '../plainText/plainText';
import { isEnterprise } from '../../utils/util';
import { DropDown } from '../../common/components/dropDown/dropDown';
import { getCurrent } from '../../common/utils/util';
import { AppContext } from '../../context/appContext';
import { getCircleEntrySetting } from '../../dataServices/getCircleEntrySetting';
import { getEnterpriseInfo } from '../../dataServices/getEnterpriseInfo';
import { VERSION_TYPE } from '../../interface/plan';
import { getRootScale } from '../../../../businessCommon/src/@utils/fontSize';

export interface IState {
    msgCnt: number;
    planId: number;
}

interface INav {
    type: string;
    class: string;
    href: string;
    title: string;
    tip?: React.ReactElement;
    target?: '_blank' | '_self';
}

let navWidthMap: { [key: string]: number } = {};
const domain = (window as any)?.pageData?.config?.site_host || location.hostname;

interface IProps {
    versionType: VERSION_TYPE;
}
const rootScale = getRootScale();

const NAV_ITEM_MARGIN_LEFT = 18 * rootScale;

export const PageHeaderNav = forwardRef(({ versionType }: IProps, ref) => {
    const appContext = useContext(AppContext);
    const {
        enterpriseInfo,
        system,
        lang,
        userInfo,
        isOnlyUseEnterpriseAccount,
        isShowVideoFileResourceModule = true,
        isShowDocumentsFileResourceModule = true,
    } = appContext.state;

    const enterpriseAccountType = Number(enterpriseInfo.role_type);
    const isEnterpriseAccount = isEnterprise(enterpriseInfo.enterprise_id);
    const showProgramAndResource = !isEnterpriseAccount || enterpriseAccountType != 1;
    const isAdmin = isEnterpriseAccount && enterpriseAccountType > 3;
    const [allNavs, setAllNavs] = useState<INav[]>([]);
    const [lineNavs, setLineNavs] = useState<INav[]>([]);
    const [drawerNavs, setdrawerNavs] = useState<INav[]>([]);
    const navsRef = useRef(allNavs);
    const isSystemCN = system === SYSTEM.CN;
    const isSystemJP = system === SYSTEM.JP;
    const isSystemTw = system === SYSTEM.TW;
    const isSystemATES = system === SYSTEM.ATES;

    const isHideMyVideo = useMemo(() => {
        if (!isShowVideoFileResourceModule) {
            return true;
        }

        return (isSystemJP || isSystemTw) && !isEnterpriseAccount;
    }, [isSystemJP, isSystemTw, isEnterpriseAccount, isShowVideoFileResourceModule]);

    const showMarket = (isSystemCN || isSystemJP) && (!isEnterpriseAccount || isAdmin) && !isSystemATES;
    const circleHref = isSystemCN ? '/learning-circle/index/owned' : '/learning-circle/index';
    const navWrapRef = useRef(null);
    let defaultNavWidth = 100 * rootScale;
    if (lang !== LANG_ENUM['zh-cn'] && lang !== LANG_ENUM['zh-tw']) {
        defaultNavWidth = 140 * rootScale;
    }

    const showUpgrade =
        versionType !== VERSION_TYPE.ENTERPRISE &&
        versionType !== VERSION_TYPE.COMMERICAL &&
        versionType !== VERSION_TYPE.INSTITUTE &&
        versionType !== VERSION_TYPE.INSTITUTE_COMMON &&
        (!isEnterpriseAccount || isAdmin) &&
        !isSystemATES;
    const [showAssessment, setShowAssessment] = useState(
        !isSystemATES && isEnterpriseAccount && enterpriseAccountType >= 2,
    );
    const [showCircle, setShowCircle] = useState(!isEnterpriseAccount && !isSystemATES);

    useImperativeHandle(ref, () => ({
        updateNavLayout() {
            updateNavLayout();
        },
    }));

    const updateNavLayout = () => {
        const navWrap = navWrapRef.current;
        if (!navWrap || !Object.keys(navWidthMap).length) {
            return;
        }
        const ulWidth = navWrap.offsetWidth;
        const lineNavList = [];
        const drawerNavList = [];
        let navsWidth = 8;

        for (let index = 0; index < navsRef.current.length; index++) {
            const nav = navsRef.current[index];
            // 元素内容宽度
            const navItemContentWidth = navWidthMap[nav.type] || defaultNavWidth;
            // 第一个li元素没有marginLeft
            const navItemMarginLeft = index === 0 ? 0 : NAV_ITEM_MARGIN_LEFT;
            // 元素内容宽度 + marginLeft
            const navItemWidth = navItemContentWidth + navItemMarginLeft;
            navsWidth += navItemWidth;
            if (navsWidth < ulWidth) {
                lineNavList.push(nav);
            } else {
                drawerNavList.push(nav);
            }
        }
        if (drawerNavList.length > 0) {
            const lineNavFirstEle = lineNavList.pop();
            if (lineNavFirstEle) {
                drawerNavList.unshift(lineNavFirstEle);
            }
        }
        setLineNavs(lineNavList);
        setdrawerNavs(drawerNavList);
    };

    useEffect(() => {
        load();
        const entity = listenResize();
        navWidthMap = {};
        return entity;
    }, []);

    useEffect(() => {
        getNavs();
    }, [showCircle, showUpgrade, versionType, showAssessment]);

    useEffect(updateNavLayout, [allNavs]);

    const isResourceCurrent =
        getCurrent('questionbank') ||
        getCurrent('videoDrive') ||
        getCurrent('templateManage') ||
        getCurrent('documentDrive');

    function listenResize() {
        window.addEventListener('resize', updateNavLayout);
        return () => {
            window.removeEventListener('resize', updateNavLayout);
        };
    }

    async function getEnterprise() {
        if (!isEnterpriseAccount) {
            return;
        }
        const res = await getEnterpriseInfo();
        if (!res.error_code && res.data) {
            setShowAssessment(res.data.permission.is_assessment === 1 && showAssessment);
        }
    }

    function load() {
        if (isEnterpriseAccount) {
            setCircleShowStatus();
        }
        getEnterprise();
    }

    function setCircleShowStatus() {
        getCircleEntrySetting().then((res) => {
            if (!res.error_code && Number(res?.data?.status) === 1) {
                setShowCircle(true && !isSystemATES);
            }
        });
    }

    const getNavClass = (type: string, extend = '') => {
        return extend + (getCurrent(type) ? ' trackEvent current' : ' trackEvent');
    };

    const renderResource = () => {
        const content = (
            <div className="sub-nav-title">
                <a className={`trackEvent ${isResourceCurrent ? 'btn current' : 'btn'}`} href="javascript:;">
                    <PlainText>课程资源</PlainText>
                </a>
            </div>
        );

        return (
            <DropDown content={content}>
                <div className={'sub-nav-items is-show'}>
                    <ul className="links">
                        <li>
                            <a className={getNavClass('questionbank')} href={`//${domain}/questionbank/`}>
                                <PlainText>题库管理</PlainText>
                            </a>
                        </li>
                        {isShowDocumentsFileResourceModule && (
                            <li>
                                <a
                                    className={getNavClass('documentDrive')}
                                    href={`//${domain}/documentDrive#/?mode=list`}
                                >
                                    <PlainText>我的文档</PlainText>
                                </a>
                            </li>
                        )}
                        {!isHideMyVideo && (
                            <li>
                                <a className={getNavClass('videoDrive')} href={`//${domain}/videoDrive#/?mode=list`}>
                                    <PlainText>我的音视频</PlainText>
                                </a>
                            </li>
                        )}
                        <li>
                            <a className={getNavClass('templateManage')} href={`//${domain}/templateManage`}>
                                <PlainText>模板管理</PlainText>
                            </a>
                        </li>
                    </ul>
                </div>
            </DropDown>
        );
    };

    const renderMarket = () => {
        const content = (
            <div className="sub-nav-title">
                <a className="trackEvent btn" href="javascript:;">
                    <PlainText>课程市场</PlainText>
                </a>
            </div>
        );

        return (
            <DropDown content={content}>
                <div className={'sub-nav-items thusem-nav-title is-show'}>
                    <ul className="links">
                        <li>
                            <a className="trackEvent" href={`//${domain}/market`}>
                                <PlainText>UMU 课程市场</PlainText>
                            </a>
                        </li>
                        <li className="thusem-li">
                            <a className="trackEvent" href={`//${domain}/thusem`}>
                                <PlainText>清华大学经济管理学院在线证书项目</PlainText>
                            </a>
                        </li>
                        <li className="thusem-li">
                            <a className="trackEvent" href={`//${domain}/mercer`}>
                                <PlainText>美世在线学习解决方案</PlainText>
                            </a>
                        </li>
                    </ul>
                </div>
            </DropDown>
        );
    };

    const getEnterpriseStudentNavs = () => {
        const navs: INav[] = [
            {
                type: 'index',
                href: '/index',
                title: '首页',
                class: getNavClass('index', 'btn'),
            },
            {
                type: 'my-tasks',
                class: getNavClass('my-tasks', 'btn'),
                href: '/my-tasks/index',
                title: '我的任务',
            },
            {
                type: 'course',
                class: getNavClass('course', 'btn'),
                href: '/course/index',
                title: '我的课程',
            },
        ];
        navs.push({
            type: 'program',
            class: getNavClass('program', 'btn'),
            href: '/program/index',
            title: '学习项目',
        });
        if (showProgramAndResource) {
            navs.push({
                type: 'resource',
                class: '',
                href: '',
                title: '课程资源',
            });
        }
        if (showAssessment) {
            navs.push({
                type: 'assessment',
                class: getNavClass('assessment', 'btn'),
                href: '/assessment/index',
                title: '我的测评',
            });
        }
        if (showCircle) {
            navs.push({
                type: 'learning-circle',
                class: getNavClass('learning-circle', 'btn'),
                href: circleHref,
                title: '学习圈',
            });
        }
        navs.push({
            type: 'class',
            class: getNavClass('class', 'btn'),
            href: '/class/index',
            title: '我的班级',
        });
        if (showMarket) {
            navs.push({
                type: 'market',
                class: getNavClass('market', 'btn'),
                href: '/market',
                title: '课程市场',
                target: '_blank',
                tip: <MarketEntryTip userId={userInfo.umu_id} isAdmin={isAdmin} />,
            });
        }
        if (showUpgrade) {
            let title = '';
            if (isSystemCN) {
                title = '升级';
            } else {
                title = '订阅';
            }
            navs.push({
                type: 'plan',
                class: getNavClass('plan', 'btn'),
                href: '/plan/pricing',
                title: title,
            });
        }
        setAllNavs(navs);
        navsRef.current = navs;
    };

    const getNavs = () => {
        const navs: INav[] = [
            {
                type: 'class',
                class: getNavClass('class', 'btn'),
                href: '/class/index',
                title: '我的班级',
            },
            {
                type: 'course',
                class: getNavClass('course', 'btn'),
                href: '/course/index',
                title: '我的课程',
            },
        ];

        if (!isOnlyUseEnterpriseAccount) {
            navs.unshift({
                type: 'index',
                href: '/index',
                title: '首页',
                class: getNavClass('index', 'btn'),
            });
        }

        navs.push({
            type: 'program',
            class: getNavClass('program', 'btn'),
            href: '/program/index',
            title: '学习项目',
        });
        navs.push({
            type: 'my-tasks',
            class: getNavClass('my-tasks', 'btn'),
            href: '/my-tasks/index',
            title: '我的任务',
        });
        if (showProgramAndResource && !isOnlyUseEnterpriseAccount) {
            navs.push({
                type: 'resource',
                class: '',
                href: '',
                title: '课程资源',
            });
        }
        if (showAssessment && !isOnlyUseEnterpriseAccount) {
            navs.push({
                type: 'assessment',
                class: getNavClass('assessment', 'btn'),
                href: '/assessment/index',
                title: '我的测评',
            });
        }
        if (showCircle && !isOnlyUseEnterpriseAccount) {
            navs.push({
                type: 'learning-circle',
                class: getNavClass('learning-circle', 'btn'),
                href: circleHref,
                title: '学习圈',
            });
        }
        if (showMarket && !isOnlyUseEnterpriseAccount) {
            navs.push({
                type: 'market',
                class: getNavClass('market', 'btn'),
                href: '/market',
                title: '课程市场',
                target: '_blank',
                tip: <MarketEntryTip userId={userInfo.umu_id} isAdmin={isAdmin} />,
            });
        }
        if (showUpgrade && !isOnlyUseEnterpriseAccount) {
            let title = '';
            if (isSystemCN) {
                title = '升级';
            } else {
                title = '订阅';
            }
            navs.push({
                type: 'plan',
                class: getNavClass('plan', 'btn'),
                href: '/plan/pricing',
                title: title,
            });
        }
        setAllNavs(navs);
        navsRef.current = navs;
    };

    const upNavWidth = (type: string, width: number) => {
        if (navWidthMap[type] !== width) {
            navWidthMap[type] = width;
            updateNavLayout();
        }
    };

    const renderLineItem = (item: INav) => {
        if (item.type === 'resource') {
            if (isOnlyUseEnterpriseAccount) {
                return null;
            }
            return (
                <NavItem key={item.type} getWidth={(width) => upNavWidth(item.type, width)}>
                    <div className="sub-nav">{renderResource()}</div>
                </NavItem>
            );
        }
        if (item.type === 'market' && isSystemCN) {
            if (isOnlyUseEnterpriseAccount) {
                return null;
            }
            return (
                <NavItem key={item.type} getWidth={(width) => upNavWidth(item.type, width)}>
                    <div className="sub-nav">{renderMarket()}</div>
                </NavItem>
            );
        }

        return (
            <NavItem key={item.type} getWidth={(width) => upNavWidth(item.type, width)}>
                <Fragment>
                    <a className={item.class} href={`//${domain}${item.href}`} target={item.target || '_self'}>
                        <PlainText>{item.title}</PlainText>
                    </a>
                    {item.tip ? item.tip : ''}
                </Fragment>
            </NavItem>
        );
    };

    const renderDrawerItem = (item: INav) => {
        if (item.type === 'resource') {
            if (isOnlyUseEnterpriseAccount) {
                return null;
            }
            return (
                <li key={item.type} className="trackEvent">
                    <div className="sub-nav">{renderResource()}</div>
                </li>
            );
        }
        if (item.type === 'market' && isSystemCN) {
            if (isOnlyUseEnterpriseAccount) {
                return null;
            }
            return (
                <li key={item.type} className="trackEvent">
                    <div className="sub-nav">{renderMarket()}</div>
                </li>
            );
        }
        return (
            <li key={item.type}>
                <a className={getNavClass(item.type)} href={item.href} target={item.target || '_self'}>
                    <PlainText>{item.title}</PlainText>
                </a>
            </li>
        );
    };

    const renderDrawer = () => {
        if (!drawerNavs.length) {
            return null;
        }
        const hasResource = drawerNavs.findIndex((v) => v.type === 'resource') !== -1;
        const current =
            (hasResource && isResourceCurrent) || drawerNavs.findIndex((nav) => getCurrent(nav.type)) !== -1;

        const content = (
            <div className="sub-nav-title">
                <a className={current ? 'btn current' : 'btn'} href="javascript:;">
                    <PlainText>更多</PlainText>
                </a>
            </div>
        );

        return (
            <li className="page-header-nav-item">
                <div className="sub-nav more">
                    <DropDown content={content}>
                        <div className={'sub-nav-items is-show'}>
                            <ul className="links">{drawerNavs.map(renderDrawerItem)}</ul>
                        </div>
                    </DropDown>
                </div>
            </li>
        );
    };

    const initialized = lineNavs.length > 0;
    return (
        <ul
            className={`page-header-nav clearfix ${appContext.state.lang} ${initialized ? '' : 'initing'}`}
            ref={navWrapRef}
        >
            {(initialized ? lineNavs : allNavs).map(renderLineItem)}
            {renderDrawer()}
        </ul>
    );
});
